import { useEffect, useRef, useCallback } from 'react';

const isMobileDevice = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const screenWidth = window.innerWidth;

  return (isMobile || (isTouchDevice && screenWidth < 768));
};

export const useDeviceEvents = (handler, options = {}) => {
  const { disabled = false, preventDefault = true } = options;
  const isMobile = useRef(isMobileDevice());
  const touchStartTimeRef = useRef(null);
  const touchEndTimeRef = useRef(null);
  const touchTargetRef = useRef(null);
  const isTouchHandledRef = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (disabled) return;

    touchStartTimeRef.current = Date.now();
    touchTargetRef.current = e.target;
    isTouchHandledRef.current = false;

    if (preventDefault) {
      e.preventDefault();
    }
  }, [disabled, preventDefault]);

  const handleTouchEnd = useCallback((e) => {
    if (disabled || isTouchHandledRef.current) return;

    touchEndTimeRef.current = Date.now();
    const touchDuration = touchEndTimeRef.current - touchStartTimeRef.current;

    if (touchDuration < 500 && e.target === touchTargetRef.current) {
      isTouchHandledRef.current = true;
      handler(e);

      if (preventDefault) {
        e.preventDefault();
      }
    }

    touchStartTimeRef.current = null;
    touchTargetRef.current = null;
  }, [disabled, handler, preventDefault]);

  const handleClick = useCallback((e) => {
    if (disabled || isTouchHandledRef.current) return;

    if (!isMobile.current) {
      handler(e);
    }
  }, [disabled, handler]);

  useEffect(() => {
    const checkDeviceType = () => {
      isMobile.current = isMobileDevice();
    };

    window.addEventListener('resize', checkDeviceType);
    return () => window.removeEventListener('resize', checkDeviceType);
  }, []);

  const eventHandlers = isMobile.current
    ? {
        onTouchStart: handleTouchStart,
        onTouchEnd: handleTouchEnd,
      }
    : {
        onClick: handleClick,
      };

  return eventHandlers;
};

export default useDeviceEvents;